import { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import { Plus, Trash2, Edit3, X, Truck, Database, AlertTriangle, MapPin, Search, Filter, Globe, BadgeCheck, ShieldCheck, Heart, Star } from "lucide-react";
import { toast } from "sonner";
import { ITALIAN_REGIONS } from "@/pages/Artists";
import BulkImportButton from "@/components/BulkImportButton";

export default function Suppliers() {
  const [items, setItems] = useState([]);
  const [favs, setFavs] = useState(new Set());
  const [favoritesOnly, setFavoritesOnly] = useState(false);
  const [editing, setEditing] = useState(null);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [region, setRegion] = useState("all");
  const [city, setCity] = useState("");
  const [maxAvg, setMaxAvg] = useState("");
  const [search, setSearch] = useState("");

  useEffect(() => {
    api.get("/cms/check-admin").then((r) => setIsSuperAdmin(!!r.data?.is_admin)).catch(() => setIsSuperAdmin(false));
    api.get("/favorites/supplier").then((r) => setFavs(new Set(r.data))).catch(() => {});
  }, []);

  const load = async () => {
    const params = {};
    if (region !== "all") params.region = region;
    if (city.trim()) params.city = city.trim();
    if (maxAvg) params.max_avg = parseFloat(maxAvg);
    if (search.trim()) params.search = search.trim();
    setItems((await api.get("/suppliers", { params })).data);
  };
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [region, city, maxAvg, search]);

  const handleSave = async (s) => {
    try {
      if (s.id) await api.put(`/suppliers/${s.id}`, s);
      else await api.post("/suppliers", s);
      toast.success("Fornitore salvato nel database condiviso");
      setEditing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };
  const handleDelete = async (id) => {
    if (!window.confirm("Eliminare questo fornitore dal database condiviso?")) return;
    try { await api.delete(`/suppliers/${id}`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  const handleReseed = async () => {
    if (!window.confirm("Ricaricare il database fornitori italiani d'esempio? (solo super admin)")) return;
    try { await api.post("/suppliers/seed"); toast.success("Database fornitori ricaricato"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  const handleClearAll = async () => {
    if (!window.confirm("⚠️ ATTENZIONE: cancellare TUTTI i fornitori? (solo super admin)")) return;
    try { await api.delete("/suppliers"); toast.success("Database fornitori svuotato"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };
  const handleToggleVerify = async (id) => {
    try {
      const r = await api.post(`/admin/verify/supplier/${id}`);
      toast.success(r.data.verified ? "Profilo verificato ✓" : "Verifica rimossa");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };
  const toggleFav = async (id) => {
    const next = new Set(favs);
    if (next.has(id)) next.delete(id); else next.add(id);
    setFavs(next);
    try {
      const r = await api.post(`/favorites/supplier/${id}`);
      toast.success(r.data.favorited ? "Aggiunto ai preferiti" : "Rimosso dai preferiti");
    } catch (e) {
      setFavs(favs);
      toast.error(e?.response?.data?.detail || "Errore");
    }
  };
  const bulkSaveVisible = async () => {
    const visibleNow = favoritesOnly ? items.filter((s) => favs.has(s.id)) : items;
    if (visibleNow.length === 0) { toast.error("Nessun fornitore visibile da salvare"); return; }
    try {
      const r = await api.post("/favorites/supplier/bulk", { entity_ids: visibleNow.map((s) => s.id) });
      toast.success(`Aggiunti ${r.data.added} ai preferiti (${r.data.already_present} già presenti)`);
      const r2 = await api.get("/favorites/supplier");
      setFavs(new Set(r2.data));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    }
  };

  const resetFilters = () => { setRegion("all"); setCity(""); setMaxAvg(""); setSearch(""); setFavoritesOnly(false); };
  const activeFilters = (region !== "all" ? 1 : 0) + (city ? 1 : 0) + (maxAvg ? 1 : 0) + (search ? 1 : 0) + (favoritesOnly ? 1 : 0);
  const visible = favoritesOnly ? items.filter((s) => favs.has(s.id)) : items;

  return (
    <div className="space-y-8" data-testid="suppliers-page">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.18em] text-[#2563EB] font-semibold font-ui">Database condiviso</p>
          <h1 className="font-display text-4xl md:text-5xl text-[#0F2741] tracking-tight mt-2">Fornitori</h1>
          <p className="text-slate-500 mt-2">{visible.length} di {items.length} fornitori · {favs.size} preferiti</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={handleClearAll} data-testid="btn-clear-suppliers" className="btn-ghost border border-rose-200 text-rose-600 hover:bg-rose-50">
            <AlertTriangle className="w-4 h-4" /> Cancella DB
          </button>
          <BulkImportButton kind="suppliers" onImported={load} />
          <button onClick={handleReseed} data-testid="btn-reseed-suppliers" className="btn-ghost border border-slate-200">
            <Database className="w-4 h-4" /> Ricarica DB
          </button>
          <button onClick={() => setEditing({})} data-testid="btn-new-supplier" className="btn-primary">
            <Plus className="w-4 h-4" /> Aggiungi al DB
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="suppliers-filters">
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <Filter className="w-4 h-4 text-[#2563EB]" />
          <p className="font-ui font-semibold text-sm text-[#0F2741]">Filtra</p>
          {activeFilters > 0 && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-[#EFF6FF] text-[#2563EB] font-semibold">{activeFilters} attivi</span>
          )}
          <button
            onClick={() => setFavoritesOnly((v) => !v)}
            data-testid="btn-toggle-suppliers-favorites-only"
            className={`ml-auto inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border transition ${favoritesOnly ? "bg-amber-100 text-amber-700 border-amber-200" : "bg-white text-slate-500 border-slate-200 hover:bg-slate-50"}`}
          >
            <Star className={`w-3.5 h-3.5 ${favoritesOnly ? "fill-amber-400 text-amber-500" : ""}`} />
            {favoritesOnly ? "Solo preferiti" : "Mostra tutti"}
          </button>
          <button
            onClick={bulkSaveVisible}
            data-testid="btn-save-visible-suppliers-favorites"
            className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-[#2563EB] text-white hover:bg-[#1D4ED8] transition"
          >
            <Heart className="w-3.5 h-3.5" /> Salva visibili come preferiti
          </button>
          {activeFilters > 0 && (
            <button onClick={resetFilters} className="text-xs text-slate-400 hover:text-[#2563EB] underline">Reset</button>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
            <input data-testid="filter-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cerca per nome" className="w-full pl-8 pr-3 py-2 rounded-lg border border-slate-200 text-sm" />
          </div>
          <select data-testid="filter-region" value={region} onChange={(e) => setRegion(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm bg-white">
            <option value="all">Tutte le regioni</option>
            {ITALIAN_REGIONS.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
          <input data-testid="filter-city" value={city} onChange={(e) => setCity(e.target.value)} placeholder="Città" className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm" />
          <div className="relative">
            <span className="absolute left-3 top-2 text-slate-400 text-sm">€</span>
            <input data-testid="filter-max-avg" type="number" min="0" step="100" value={maxAvg} onChange={(e) => setMaxAvg(e.target.value)} placeholder="Spesa media max" className="w-full pl-7 pr-3 py-2 rounded-lg border border-slate-200 text-sm" />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {visible.length === 0 && (
          <div className="col-span-full bg-white rounded-xl border border-dashed border-slate-300 p-12 text-center">
            <Truck className="w-12 h-12 mx-auto text-slate-300 mb-2" />
            <p className="text-slate-500">{favoritesOnly ? "Nessun fornitore tra i tuoi preferiti." : "Nessun fornitore. Aggiungine uno o caricalo dalla pagina OCR Fatture."}</p>
          </div>
        )}
        {visible.map((s) => {
          const isFav = favs.has(s.id);
          return (
          <div key={s.id} className="bg-white rounded-xl border border-slate-200 p-5 card-hover relative" data-testid={`supplier-card-${s.id}`}>
            <button
              onClick={() => toggleFav(s.id)}
              data-testid={`btn-fav-supplier-${s.id}`}
              className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-slate-50 transition"
              title={isFav ? "Rimuovi dai preferiti" : "Aggiungi ai preferiti"}
            >
              <Heart className={`w-5 h-5 ${isFav ? "fill-rose-500 text-rose-500" : "text-slate-300"}`} />
            </button>
            <div className="flex justify-between items-start pr-8">
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-ui font-semibold text-lg text-[#0F2741]">{s.name}</h3>
                  {s.verified && (
                    <span title="Profilo verificato dal team Event System 360" className="inline-flex items-center gap-0.5 text-xs px-1.5 py-0.5 rounded-full bg-sky-100 text-sky-700 font-semibold">
                      <BadgeCheck className="w-3 h-3" /> Verificato
                    </span>
                  )}
                </div>
                {s.category && <p className="text-xs uppercase tracking-wider text-[#1D4ED8] mt-0.5">{s.category}</p>}
                {(s.contact || s.phone || s.email) && (
                  <p className="text-sm text-slate-500 mt-1">{[s.contact, s.phone, s.email].filter(Boolean).join(" · ")}</p>
                )}
                {s.website && (
                  <a href={s.website} target="_blank" rel="noreferrer" className="text-xs text-[#2563EB] hover:text-[#1D4ED8] mt-0.5 inline-flex items-center gap-1"><Globe className="w-3 h-3" /> {s.website.replace(/^https?:\/\//, "").replace(/\/$/, "")}</a>
                )}
                {(s.city || s.region) && (
                  <p className="text-xs text-slate-500 mt-1 inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {[s.city, s.region].filter(Boolean).join(" · ")}</p>
                )}
                {s.address && <p className="text-xs text-slate-400 mt-0.5">{s.address}</p>}
                {Number(s.avg_order_eur) > 0 && (
                  <p className="text-xs text-[#1D4ED8] mt-1 font-semibold">Spesa media: €{Number(s.avg_order_eur).toFixed(0)} / ordine</p>
                )}
              </div>
              <span className={`text-xs px-2 py-1 rounded-full shrink-0 ${s.active ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                {s.active ? "Attivo" : "Inattivo"}
              </span>
            </div>
            {Object.keys(s.price_list || {}).length > 0 && (
              <div className="mt-4 pt-4 border-t border-slate-100">
                <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">Listino ({Object.keys(s.price_list || {}).length} voci)</p>
                <div className="flex flex-wrap gap-1.5">
                  {Object.entries(s.price_list || {}).slice(0, 6).map(([k, v]) => (
                    <span key={k} className="text-xs px-2 py-1 rounded-full bg-[#EFF6FF] text-[#1D4ED8]">{k}: €{Number(v).toFixed(2)}/kg</span>
                  ))}
                  {Object.keys(s.price_list || {}).length > 6 && <span className="text-xs text-slate-400">+{Object.keys(s.price_list).length - 6}</span>}
                </div>
              </div>
            )}
            <div className="mt-3 flex gap-2">
              <button onClick={() => setEditing(s)} className="btn-ghost flex-1"><Edit3 className="w-3.5 h-3.5" /> Modifica</button>
              {isSuperAdmin && (
                <button onClick={() => handleToggleVerify(s.id)} data-testid={`btn-verify-supplier-${s.id}`}
                  title={s.verified ? "Rimuovi verifica" : "Verifica profilo"}
                  className={`btn-ghost ${s.verified ? "text-sky-600 border border-sky-200" : "text-slate-500 border border-slate-200"}`}>
                  <ShieldCheck className="w-3.5 h-3.5" />
                </button>
              )}
              <button onClick={() => handleDelete(s.id)} className="btn-ghost text-rose-500"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          </div>
          );
        })}
      </div>

      {editing !== null && <SupplierModal initial={editing} onClose={() => setEditing(null)} onSave={handleSave} />}
    </div>
  );
}

function SupplierModal({ initial, onClose, onSave }) {
  const [form, setForm] = useState({
    id: initial.id, name: initial.name || "", category: initial.category || "", contact: initial.contact || "",
    phone: initial.phone || "", email: initial.email || "",
    website: initial.website || "",
    address: initial.address || "",
    city: initial.city || "",
    region: initial.region || "",
    avg_order_eur: initial.avg_order_eur || 0,
    active: initial.active ?? true,
  });
  // price_list: { ingredient_name_lower: price }
  const [priceList, setPriceList] = useState(() => {
    const out = {};
    for (const [k, v] of Object.entries(initial.price_list || {})) out[k.toLowerCase()] = parseFloat(v) || 0;
    return out;
  });
  const [ingredients, setIngredients] = useState([]);
  const [filterCat, setFilterCat] = useState("all");
  const [search, setSearch] = useState("");

  useEffect(() => {
    api.get("/ingredients").then((r) => setIngredients(r.data));
  }, []);

  const visible = ingredients.filter((i) => {
    if (filterCat !== "all" && i.category !== filterCat) return false;
    if (search && !i.name.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const categories = useMemo(() => Array.from(new Set(ingredients.map((i) => i.category))).sort(), [ingredients]);

  const isInList = (name) => Object.prototype.hasOwnProperty.call(priceList, name.toLowerCase());
  const allVisibleSelected = visible.length > 0 && visible.every((i) => isInList(i.name));

  const toggleOne = (i) => {
    const k = i.name.toLowerCase();
    setPriceList((prev) => {
      const next = { ...prev };
      if (Object.prototype.hasOwnProperty.call(next, k)) delete next[k];
      else next[k] = i.price_eur || 0;
      return next;
    });
  };

  const toggleAllVisible = () => {
    setPriceList((prev) => {
      const next = { ...prev };
      if (allVisibleSelected) {
        for (const i of visible) delete next[i.name.toLowerCase()];
      } else {
        for (const i of visible) {
          if (!Object.prototype.hasOwnProperty.call(next, i.name.toLowerCase())) {
            next[i.name.toLowerCase()] = i.price_eur || 0;
          }
        }
      }
      return next;
    });
  };

  const setPrice = (name, val) => {
    setPriceList((prev) => ({ ...prev, [name.toLowerCase()]: parseFloat(val) || 0 }));
  };

  const removeFromList = (name) => {
    setPriceList((prev) => {
      const next = { ...prev };
      delete next[name.toLowerCase()];
      return next;
    });
  };

  const submit = (e) => {
    e.preventDefault();
    onSave({ ...form, price_list: priceList });
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 grid place-items-center p-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="bg-white rounded-xl p-6 w-full max-w-4xl space-y-4 max-h-[90vh] overflow-y-auto" data-testid="supplier-modal">
        <div className="flex justify-between items-center sticky top-0 bg-white pb-2 z-10">
          <h3 className="font-display text-2xl text-[#0F2741]">{form.id ? "Modifica" : "Nuovo"} fornitore</h3>
          <button type="button" onClick={onClose}><X className="w-5 h-5 text-slate-400" /></button>
        </div>

        {/* Dati anagrafici */}
        <div className="space-y-3">
          <p className="text-xs text-slate-500">I fornitori sono in un <strong>database condiviso</strong>: ciò che aggiungi sarà visibile a tutti gli organizzatori.</p>
          <input required value={form.name} placeholder="Ragione sociale" data-testid="input-supplier-name" onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-slate-200" />
          <input value={form.category} placeholder="Categoria (es: Salumi, Beverage Horeca, Cash & Carry)" data-testid="input-supplier-category" onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-slate-200" />
          <div className="grid grid-cols-2 gap-3">
            <input value={form.contact} placeholder="Referente" onChange={(e) => setForm({ ...form, contact: e.target.value })} className="px-3 py-2 rounded-lg border border-slate-200" />
            <input value={form.phone} placeholder="Telefono" onChange={(e) => setForm({ ...form, phone: e.target.value })} className="px-3 py-2 rounded-lg border border-slate-200" />
          </div>
          <input value={form.email} placeholder="Email" type="email" onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-slate-200" />
          <input value={form.website} placeholder="Sito web (https://...)" data-testid="input-supplier-website" onChange={(e) => setForm({ ...form, website: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-slate-200" />
          <input value={form.address} placeholder="Indirizzo" data-testid="input-supplier-address" onChange={(e) => setForm({ ...form, address: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-slate-200" />
          <div className="grid grid-cols-2 gap-3">
            <input value={form.city} placeholder="Città" data-testid="input-supplier-city" onChange={(e) => setForm({ ...form, city: e.target.value })} className="px-3 py-2 rounded-lg border border-slate-200" />
            <select value={form.region} data-testid="input-supplier-region" onChange={(e) => setForm({ ...form, region: e.target.value })} className="px-3 py-2 rounded-lg border border-slate-200 bg-white">
              <option value="">Regione</option>
              {ITALIAN_REGIONS.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <label className="block">
            <span className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Spesa media per ordine (€)</span>
            <input type="number" step="50" min="0" value={form.avg_order_eur} data-testid="input-supplier-avg" onChange={(e) => setForm({ ...form, avg_order_eur: parseFloat(e.target.value) || 0 })} className="w-full px-3 py-2 rounded-lg border border-slate-200 mt-1 font-semibold" />
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="accent-[#2563EB]" />
            Fornitore attivo
          </label>
        </div>

        {/* Listino dal database ingredienti */}
        <div className="border-t border-slate-200 pt-4">
          <div className="flex justify-between items-center mb-3">
            <div>
              <h4 className="font-ui font-semibold text-[#0F2741]">Listino dal database ingredienti</h4>
              <p className="text-xs text-slate-500">{Object.keys(priceList).length} ingredienti nel listino · {ingredients.length} totali nel DB</p>
            </div>
          </div>

          <div className="flex flex-wrap gap-2 mb-3">
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cerca ingrediente..." className="flex-1 min-w-[200px] px-3 py-2 rounded-lg border border-slate-200 text-sm" />
            <select value={filterCat} onChange={(e) => setFilterCat(e.target.value)} className="px-3 py-2 rounded-lg border border-slate-200 text-sm">
              <option value="all">Tutte le categorie</option>
              {categories.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <button type="button" onClick={toggleAllVisible} data-testid="btn-select-all-ingredients" className="btn-ghost border border-slate-200">
              {allVisibleSelected ? "Deseleziona tutti" : "Seleziona tutti"} ({visible.length})
            </button>
          </div>

          <div className="border border-slate-200 rounded-lg overflow-hidden max-h-72 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase text-slate-500 sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left w-8">
                    <input type="checkbox" checked={allVisibleSelected} onChange={toggleAllVisible} className="accent-[#2563EB]" data-testid="checkbox-select-all" />
                  </th>
                  <th className="px-3 py-2 text-left">Ingrediente</th>
                  <th className="px-3 py-2 text-left">Categoria</th>
                  <th className="px-3 py-2 text-right">€ mercato</th>
                  <th className="px-3 py-2 text-right">Prezzo fornitore</th>
                </tr>
              </thead>
              <tbody>
                {visible.length === 0 ? (
                  <tr><td colSpan={5} className="px-3 py-6 text-center text-slate-400 text-sm">Nessun ingrediente</td></tr>
                ) : visible.map((i) => {
                  const inList = isInList(i.name);
                  return (
                    <tr key={i.id} className={`border-t border-slate-100 ${inList ? "bg-[#EFF6FF]" : ""}`}>
                      <td className="px-3 py-2">
                        <input type="checkbox" checked={inList} onChange={() => toggleOne(i)} data-testid={`chk-ing-${i.id}`} className="accent-[#2563EB]" />
                      </td>
                      <td className="px-3 py-2 capitalize">{i.name}</td>
                      <td className="px-3 py-2 text-xs text-slate-500 capitalize">{i.category}</td>
                      <td className="px-3 py-2 text-right text-xs text-slate-500">€{Number(i.price_eur).toFixed(2)}/{i.unit}</td>
                      <td className="px-3 py-2 text-right">
                        {inList ? (
                          <input
                            type="number" step="0.10" min="0"
                            value={priceList[i.name.toLowerCase()]}
                            onChange={(e) => setPrice(i.name, e.target.value)}
                            className="w-24 px-2 py-1 rounded border border-slate-200 text-right text-sm focus:ring-2 focus:ring-[#2563EB] outline-none"
                          />
                        ) : (
                          <span className="text-xs text-slate-300">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Riepilogo voci listino fuori filtro */}
          {Object.keys(priceList).length > 0 && (
            <div className="mt-3 p-3 rounded-lg bg-slate-50 border border-slate-200">
              <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">Listino completo ({Object.keys(priceList).length})</p>
              <div className="flex flex-wrap gap-1.5">
                {Object.entries(priceList).map(([k, v]) => (
                  <span key={k} className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-white border border-[#2563EB]/30 text-xs">
                    <span className="capitalize">{k}</span>
                    <span className="text-[#1D4ED8] font-semibold">€{Number(v).toFixed(2)}</span>
                    <button type="button" onClick={() => removeFromList(k)} className="text-slate-400 hover:text-rose-500 ml-1"><X className="w-3 h-3" /></button>
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 sticky bottom-0 bg-white pt-3">
          <button type="button" onClick={onClose} className="btn-ghost">Annulla</button>
          <button type="submit" data-testid="btn-save-supplier" className="btn-primary">Salva fornitore</button>
        </div>
      </form>
    </div>
  );
}
